import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { getCompanyInfo, getServices } from "../services/api";
import { useToast } from "../hooks/use-toast";

const ContactPage = () => {
  const { toast } = useToast();
  const [companyInfo, setCompanyInfo] = useState({
    phone: "1-888-907-3777",
    email: "hello@groundhogs.ca",
    address: {
      street: "415 - Unit B, Fort William Road",
      city: "Thunder Bay",
      province: "Ontario",
      postalCode: "P7B 2Z5"
    }
  });
  const [serviceOptions, setServiceOptions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    serviceType: "",
    message: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyInfoData, servicesData] = await Promise.all([
          getCompanyInfo(),
          getServices(),
        ]);
        setCompanyInfo(companyInfoData);

        // Build dropdown options from live services, excluding any archived ones by backend filter
        const options = servicesData.map((service) => ({
          value: service.slug,
          label: service.title,
        }));
        setServiceOptions(options);
      } catch (error) {
        console.error("Error fetching contact page data:", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "Request Submitted!",
          description: "We&apos;ll contact you within 24 hours to provide your free virtual consultation and estimate."
        });
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          serviceType: "",
          message: ""
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast({
        title: "Submission Error",
        description: "Please call us at 1-888-907-3777 or email hello@groundhogs.ca",
        variant: "destructive"
      });
    }
  };

  return (
    <div>
      <SEO 
        title="Contact Groundhogs Septic | Thunder Bay"
        description="Contact Thunder Bay's fastest septic installers. Free estimates, 24/7 emergency service. Call 1-888-907-3777 or email hello@groundhogs.ca"
        keywords="contact Groundhogs Septics, Thunder Bay septic contact, septic installation quote"
      />
      <Header />

      {/* Hero Section */}
      <section className="gh-intro pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-intro-heading text-center mb-12" data-reveal>
            <h1 className="text-5xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Get Your Free Estimate
            </h1>
            <p className="text-xl" style={{ color: "var(--primary-orange)" }}>
              Northwestern Ontario&apos;s Trusted Septic Installation Experts
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <Phone size={40} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-3" />
              <h3 className="text-lg font-bold mb-2" style={{ color: "var(--dark-navy)" }}>Call Us</h3>
              <a href={`tel:${companyInfo.phone}`} className="text-xl font-bold hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                {companyInfo.phone}
              </a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <Mail size={40} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-3" />
              <h3 className="text-lg font-bold mb-2" style={{ color: "var(--dark-navy)" }}>Email Us</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-lg hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                {companyInfo.email}
              </a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <Clock size={40} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-3" />
              <h3 className="text-lg font-bold mb-2" style={{ color: "var(--dark-navy)" }}>Business Hours</h3>
              <p style={{ color: "var(--text-light)" }}>Monday - Friday</p>
              <p style={{ color: "var(--text-light)" }}>9:00 AM - 5:00 PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-contact-layout grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
                Contact Request
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>Full Name *</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                    style={{ borderColor: "var(--border)", focusRing: "var(--primary-orange)" }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>Email *</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                      style={{ borderColor: "var(--border)" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>Phone *</label>
                    <input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                      style={{ borderColor: "var(--border)" }}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-address" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>Street Address *</label>
                  <input
                    id="contact-address"
                    data-testid="contact-address-input"
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 123 Lakeshore Drive"
                    className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                    style={{ borderColor: "var(--border)" }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-city" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>City/Town *</label>
                  <input
                    id="contact-city"
                    data-testid="contact-city-input"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Thunder Bay, Kenora, Dryden"
                    className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                    style={{ borderColor: "var(--border)" }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-service" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>Service Needed *</label>
                  <select
                    id="contact-service"
                    data-testid="contact-service-select"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <option value="">Select a service...</option>
                    {serviceOptions.map((service) => (
                      <option key={service.value} value={service.value}>
                        {service.label}
                      </option>
                    ))}
                    <option value="not-sure">Not Sure - Need Consultation</option>
                  </select>
                  <p className="mt-1 text-xs" style={{ color: "var(--text-light)" }}>
                    These options match our current septic services listed on the website.
                  </p>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium mb-2" style={{ color: "var(--text-dark)" }}>Message</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2"
                    style={{ borderColor: "var(--border)" }}
                    placeholder="Tell us about your property and project..."
                  />
                </div>
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  className="w-full px-8 py-4 rounded-md font-bold text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-orange)" }}
                >
                  Submit Request
                </button>
                <p className="text-sm text-center" style={{ color: "var(--text-light)" }}>
                  We&apos;ll contact you within 24 hours to provide your free virtual consultation and estimate
                </p>
              </form>
            </div>

            {/* Map & Address Info */}
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
                Visit Our Headquarters
              </h2>
              <div className="bg-white border-2 rounded-lg p-6 mb-6" style={{ borderColor: "var(--primary-orange)" }}>
                <div className="flex items-start space-x-4">
                  <MapPin size={32} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                      Main Headquarters
                    </h3>
                    <p style={{ color: "var(--text-dark)" }}>
                      {companyInfo.address.street}<br />
                      {companyInfo.address.city}, {companyInfo.address.province}<br />
                      {companyInfo.address.postalCode}
                    </p>
                    <div className="mt-4">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address.street + ', ' + companyInfo.address.city + ', ' + companyInfo.address.province)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-6 py-2 rounded-md font-medium text-white hover:opacity-90"
                        style={{ backgroundColor: "var(--primary-orange)" }}
                      >
                        Get Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Service Area Map */}
              <div className="gh-contact-map h-96 bg-gray-100 rounded-lg overflow-hidden border-2" style={{ borderColor: "var(--primary-orange)" }}>
                <img 
                  src="/service-area-map.png" 
                  alt="Groundhogs Septics Service Area Map - Northwestern Ontario"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Notice */}
      <section className="py-12" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
            Serving All of Northwestern Ontario
          </h3>
          <p className="text-lg" style={{ color: "var(--text-light)" }}>
            Thunder Bay • Kenora • Dryden • Fort Frances • Atikokan • Red Lake • Sioux Lookout • Ignace & Surrounding Areas
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;